import React, { useState, useMemo, useEffect } from 'react';
import { Target, TrendingUp, Filter, ArrowUpDown, ArrowUp, ArrowDown, Trophy, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/ApiUrl';
import { THCCalculatorModal } from '../components/THCCalculatorModal';
import { useTheme } from '../arrangement/ThemeContext';
export function THCLeaderboardPage() {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('thc_mg_per_dollar');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedRows, setExpandedRows] = useState({}); // Track expanded rows
  const [showCalculator, setShowCalculator] = useState(false);
  

  const { colors } = useTheme();

  const [expandedMobileCards, setExpandedMobileCards] = useState({});

  
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadLeaderboard();
  }, [currentPage, sortBy, sortDirection, filterType]);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '50',
        sort: sortBy,
        direction: sortDirection,
        ...(filterType !== 'all' && { type: filterType })
      });

      const response = await fetch(`${API_URL}/api/thc-value-leaderboard?${params}`);
      const data = await response.json();
      
      setLeaderboardData(data.results || []);
      setTotalPages(data.total_pages || 1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(column);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown size={14} style={{ color: '#666' }} />;
    return sortDirection === 'desc' ? 
      <ArrowDown size={14} style={{ color: '#f59e0b' }} /> : 
      <ArrowUp size={14} style={{ color: '#f59e0b' }} />;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <span style={{ fontSize: '18px' }}>🥇</span>;
    if (rank === 2) return <span style={{ fontSize: '18px' }}>🥈</span>;
    if (rank === 3) return <span style={{ fontSize: '18px' }}>🥉</span>;
    return <span style={{ fontSize: '14px', fontWeight: '600', color: '#888' }}>#{rank}</span>;
  };

const getRowStyle = (rank) => {
  // Keep special styling for top 3 (gold/silver/bronze)
  if (rank === 1) return {
    background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%)',
    border: '1px solid rgba(255, 215, 0, 0.3)'
  };
  if (rank === 2) return {
    background: 'linear-gradient(135deg, rgba(192, 192, 192, 0.1) 0%, rgba(192, 192, 192, 0.05) 100%)',
    border: '1px solid rgba(192, 192, 192, 0.3)'
  };
  if (rank === 3) return {
    background: 'linear-gradient(135deg, rgba(205, 127, 50, 0.1) 0%, rgba(205, 127, 50, 0.05) 100%)',
    border: '1px solid rgba(205, 127, 50, 0.3)'
  };
  
  // For other ranks, use subtle alternating backgrounds
  return {
    background: rank % 2 === 0 ? `${colors.bgCard}CC` : `${colors.bgCard}80`,
    border: `1px solid ${colors.border}`
  };
};
  const renderStarRating = (rating, reviewCount) => {
    if (!rating) {
      return (
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '4px',
          color: '#666',
          fontSize: '12px'
        }}>
          <Star size={12} style={{ color: '#666' }} />
          No reviews
        </div>
      );
    }

    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star 
            key={i} 
            size={12} 
            style={{ color: '#f59e0b', fill: '#f59e0b' }} 
          />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
            <Star size={12} style={{ color: '#333' }} />
            <div style={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              width: '50%', 
              overflow: 'hidden' 
            }}>
              <Star size={12} style={{ color: '#f59e0b', fill: '#f59e0b' }} />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star 
            key={i} 
            size={12} 
            style={{ color: '#333' }} 
          />
        );
      }
    }

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ display: 'flex', gap: '1px' }}>
          {stars}
        </div>
        <span style={{ 
          color: '#e5e5e5', 
          fontSize: '11px', 
          fontWeight: '600',
          marginLeft: '4px'
        }}>
          {rating.toFixed(1)}
        </span>
        <span style={{ 
          color: '#888', 
          fontSize: '10px',
          marginLeft: '2px'
        }}>
          ({reviewCount})
        </span>
      </div>
    );
  };

  const formatProductName = (name, maxFirstLine = 25, maxSecondLine = 20) => {
    if (name.length <= maxFirstLine) return { line1: name, line2: '' };
    
    const words = name.split(' ');
    let line1 = '';
    let line2 = '';
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line1 + (line1 ? ' ' : '') + words[i];
      if (testLine.length <= maxFirstLine && i < words.length - 1) {
        line1 = testLine;
      } else {
        line2 = words.slice(line1 ? i : 0).join(' ');
        break;
      }
    }
    
    if (line2.length > maxSecondLine) {
      line2 = line2.substring(0, maxSecondLine - 3) + '...';
    }
    
    return { line1, line2 };
  };

  const SmokerLevelStats = ({ smokerReviews }) => {
    const smokerStats = useMemo(() => {
      const smokerLevels = ['beginner', 'casual', 'connoisseur', 'stoner'];
      return smokerLevels.reduce((acc, level) => {
        const reviews = smokerReviews?.filter(
          (review) => review.smoker_level.toLowerCase() === level
        ) || [];
        const count = reviews.length;
        const averageRating = count > 0
          ? (reviews.reduce((sum, review) => sum + review.rating, 0) / count).toFixed(1)
          : '0.0';
        acc[level] = { count, averageRating };
        return acc;
      }, {});
    }, [smokerReviews]);

    const smokerLevelData = [
      {
        level: 'Beginner',
        image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/cdb53b7a8c7e4624a810c0d654ca03c8_Smoker_beginer.webp',
        color: '#8b5cf6',
        stats: smokerStats.beginner,
      },
      {
        level: 'Casual',
        image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/e72017f33d1c480db1928ca3d7295de4_Smoker_Level_cas.webp',
        color: '#ef4444',
        stats: smokerStats.casual,
      },
      {
        level: 'Connoisseur',
        image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/3e1e13711dd441fcb7c34c155416ee69_Smoker_Level_con.webp',
        color: '#00d4aa',
        stats: smokerStats.connoisseur,
      },
      {
        level: 'Stoner',
        image: 'https://highmind-bucket.s3.us-east-1.amazonaws.com/9084554e27d747188148530bc7899eee_Smoker_Level_Logo_Stoner.webp',
        color: '#4480ef',
        stats: smokerStats.stoner,
      },
    ];

    return (
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '8px',
        padding: '8px',
        background: colors.bgCard,
        borderRadius: '8px',
        fontSize: '10px',
      }}>
        {smokerLevelData.map(({ level, image, color, stats }) => (
          <div key={level} style={{ textAlign: 'center' }}>
            <img
              src={image}
              alt={level}
              style={{ width: '2.5rem', height: '2.5rem', marginBottom: '4px' }}
            />
            <div style={{ color: colors.textSecondary }}>{level}</div>
            <div style={{ color, fontWeight: '600' }}>
              {stats.averageRating}/5 ({stats.count})
            </div>
          </div>
        ))}
      </div>
    );
  };

  const HybridMobileCard = ({ item, rank, index }) => { // ← Add index prop
    const cardStyle = getRowStyle(rank);
    const { line1, line2 } = formatProductName(item.product?.name);
    const hasReviews = item.review_count > 0;
    const showSmokerStats = expandedMobileCards[index] ?? hasReviews;

    return (
      <div style={{
        ...cardStyle,
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '12px',
        minHeight: '160px'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            minWidth: '36px',
            height: '36px'
          }}>
            {getRankIcon(rank)}
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '12px',
            flex: 1,
            minWidth: 0
          }}>
            <img
              src={item.product?.image || '/placeholder-product.jpg'}
              alt={item.product?.name}
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '8px',
                objectFit: 'cover',
                border: '1px solid #333',
                flexShrink: 0
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                margin: '0 0 4px 0',
                fontSize: '15px',
                fontWeight: '600',
                color: '#e5e5e5',
                lineHeight: '1.3'
              }}>
                <div>{line1}</div>
                {line2 && <div>{line2}</div>}
              </div>
              <p style={{
                margin: '0 0 6px 0',
                fontSize: '13px',
                color: '#888',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {item.product?.brand}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  background: 'rgba(0, 212, 170, 0.2)',
                  color: colors.textPrimary,
                  padding: '3px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {item.product?.type || 'N/A'}
                </span>
                <div style={{
                  padding: '3px 8px',
                  background: `linear-gradient(135deg, ${
                    rank <= 10 ? '#00d4aa' : rank <= 25 ? '#f59e0b' : '#666'
                  } 0%, ${
                    rank <= 10 ? '#10b981' : rank <= 25 ? '#d97706' : '#555'
                  } 100%)`,
                  color: rank <= 25 ? '#0a0a0a' : 'white',
                  borderRadius: '6px',
                  fontSize: '10px',
                  fontWeight: '600'
                }}>
                  {rank <= 10 ? 'ELITE' : rank <= 25 ? 'GREAT' : 'GOOD'}
                </div>
              </div>
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '8px 12px',
            background: 'rgba(245, 158, 11, 0.15)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            minWidth: '70px',
            transform: 'translateY(1.5rem)'
          }}>
            <div style={{
              fontSize: '20px',
              fontWeight: '800',
              color: rank <= 3 ? '#f59e0b' : '#e5e5e5',
              lineHeight: '1'
            }}>
              {item.thc_mg_per_dollar?.toFixed(1) || '0'}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#888',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginTop: '2px'
            }}>
              THC mg/$
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '12px',
          marginBottom: '12px'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '10px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#e5e5e5',
              marginBottom: '2px'
            }}>
              {item.thc_mg_total?.toFixed(1) || '0'}mg
            </div>
            <div style={{
              fontSize: '10px',
              color: '#888',
              textTransform: 'uppercase'
            }}>
              Total THC
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '10px',
            background: 'rgba(0, 212, 170, 0.1)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#00d4aa',
              marginBottom: '2px'
            }}>
              ${item.price_dollars?.toFixed(2) || '0.00'}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#888',
              textTransform: 'uppercase'
            }}>
              Price
            </div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '10px',
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#e5e5e5',
              marginBottom: '2px'
            }}>
              ${item.price_per_mg_thc?.toFixed(3) || '0.000'}
            </div>
            <div style={{
              fontSize: '10px',
              color: '#888',
              textTransform: 'uppercase'
            }}>
              Per mg THC
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '12px',
          borderTop: '1px solid #333'
        }}>
          <button
            onClick={() => setExpandedMobileCards(prev => ({
              ...prev,
              [index]: !prev[index]
            }))}
            style={{
              background: 'none',
              border: 'none',
              color: '#00d4aa',
              cursor: 'pointer',
              fontSize: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
            aria-label={showSmokerStats ? 'Hide smoker level reviews' : 'Show smoker level reviews'}
          >
            {showSmokerStats ? 'Hide Smoker Reviews' : 'Show Smoker Reviews'}
            {showSmokerStats ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
          </button>
        </div>
        {showSmokerStats && hasReviews && (
          <div style={{ marginTop: '12px' }}>
            <SmokerLevelStats smokerReviews={item.smoker_reviews} />
          </div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '12px',
          borderTop: showSmokerStats ? '1px solid #333' : 'none'
        }}>
          {renderStarRating(item.avg_rating, item.review_count)}
        </div>
      </div>
    );
  };

  const MobileSortControls = () => (
    <div style={{
      background: colors.bgTertiary,
      border: '1px solid #333',
      borderRadius: '12px',
      padding: '16px',
      marginBottom: '24px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px'
      }}>
        <ArrowUpDown size={16} style={{ color: '#888' }} />
        <span style={{ color: '#888', fontSize: '14px' }}>Sort by:</span>
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: '#2a2a2a',
            border: '1px solid #333',
            borderRadius: '8px',
            color: '#e5e5e5',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        >
          <option value="thc_mg_per_dollar">Best Value (THC mg/$)</option>
          <option value="price_per_mg_thc">Cheapest per mg</option>
          <option value="thc_mg_total">Strongest (Total THC)</option>
          <option value="price_dollars">Lowest Price</option>
          <option value="avg_rating">Highest Rated</option>
          <option value="product_name">Product Name</option>
        </select>

        <button
          onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
          style={{
            padding: '12px',
            background: '#333',
            color: '#e5e5e5',
            border: '1px solid #444',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '48px'
          }}
        >
          {sortDirection === 'desc' ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
        </button>
      </div>
    </div>
  );

  const toggleRow = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            fontSize: '48px',
            marginBottom: '16px',
            animation: 'spin 1s linear infinite'
          }}>🏆</div>
          <p style={{ color: '#888' }}>Loading THC Value Leaderboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ef4444', marginBottom: '16px' }}>Error Loading Leaderboard</h2>
          <p style={{ color: '#888', marginBottom: '20px' }}>{error}</p>
          <button
            onClick={() => navigate('/products')}
            style={{
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '8px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.bgPrimary,
      color: colors.textPrimary,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .table-container {
          overflow-x: auto;
          border-radius: 12px;
          border: 1px solid ${colors.border};
          background: ${colors.bgTertiary};
        }
        
        .leaderboard-table {
          width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          min-width: 1100px;
        }
        
        .leaderboard-table th {
          background: linear-gradient(135deg, ${colors.bgCard} 0%, ${colors.bgTertiary} 100%);
          padding: 16px 12px;
          text-align: left;
          font-weight: 600;
          font-size: 14px;
          color: ${colors.textPrimary};
          border-bottom: 1px solid ${colors.border};
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .leaderboard-table td {
          padding: 16px 12px;
          border-bottom: 1px solid ${colors.border};
          font-size: 14px;
          vertical-align: middle;
        }
        
        .sort-header {
          cursor: pointer;
          user-select: none;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: all 0.2s ease;
        }
        
        .sort-header:hover {
          color: #f59e0b;
        }
        
        .product-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        
        .product-image {
          width: 40px;
          height: 40px;
          border-radius: 8px;
          object-fit: cover;
          border: 1px solid #333;
        }
        
        .product-info h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: #e5e5e5;
        }
        
        .product-info p {
          margin: 0;
          font-size: 12px;
          color: #888;
        }

        .expand-button {
          background: none;
          border: none;
          color: #00d4aa;
          cursor: pointer;
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '16px' : '32px 24px' }}>
        <div style={{ marginBottom: '32px' }}>
          <button
            onClick={() => navigate('/products')}
            style={{
              background: 'none',
              border: 'none',
              color: '#00d4aa',
              cursor: 'pointer',
              fontSize: '14px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            ← Back to Products
          </button>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Trophy size={32} style={{ color: '#f59e0b' }} />
            <h1 style={{
              fontSize: isMobile ? '24px' : '32px',
              fontWeight: '800',
              margin: 0,
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              THC Value Leaderboard
            </h1>
          </div>
          
          <p style={{ margin: 0, color: '#888', fontSize: isMobile ? '14px' : '16px' }}>
            Best THC value per dollar - Find the most potent deals in your area
          </p>
          <button
            onClick={() => setShowCalculator(true)}
            style={{
              padding: isMobile ? '10px 16px' : '12px 20px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: '#0a0a0a',
              border: 'none',
              borderRadius: '8px',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'inline-flex',
              marginTop: '1rem',
              alignItems: 'center',
              gap: '8px',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(245, 158, 11, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            Calculate Your Own & Compare
          </button>
        </div>

        <div style={{
          background: colors.bgTertiary,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Filter size={16} style={{ color: colors.textSecondary }} />
            <span style={{ color: colors.textSecondary, fontSize: '14px' }}>Filter by:</span>
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            style={{
              padding: '8px 12px',
              backgroundColor: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '8px',
              color: colors.textPrimary,
              fontSize: '14px',
              cursor: 'pointer',
              ...(isMobile && { width: '100%' })
            }}
          >
            <option value="all">All Products</option>
            <option value="flower">Flower</option>
            <option value="cart">Cartridges</option>
            <option value="edible">Edibles</option>
            <option value="pre-roll">Pre-rolls</option>
            <option value="concentrate">Concentrates</option>
          </select>
        </div>

        {isMobile && <MobileSortControls />}

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {leaderboardData.map((item, index) => {
              const rank = (currentPage - 1) * 50 + index + 1;
              return <HybridMobileCard key={index} item={item} rank={rank} index={index} />; // ← Pass index
            })}
          </div>
        ) : (
          <div className="table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Rank</th>
                  <th style={{ minWidth: '250px' }}>
                    <div className="sort-header" onClick={() => handleSort('product_name')}>
                      Product
                      {getSortIcon('product_name')}
                    </div>
                  </th>
                  <th style={{ minWidth: '150px' }}>Smoker Level Review</th>
                  <th>
                    <div className="sort-header" onClick={() => handleSort('product_type')}>
                      Type
                      {getSortIcon('product_type')}
                    </div>
                  </th>
                  <th>
                    <div className="sort-header" onClick={() => handleSort('thc_mg_total')}>
                      Total THC (mg)
                      {getSortIcon('thc_mg_total')}
                    </div>
                  </th>
                  <th>
                    <div className="sort-header" onClick={() => handleSort('price_dollars')}>
                      Price
                      {getSortIcon('price_dollars')}
                    </div>
                  </th>
                  <th>
                    <div className="sort-header" onClick={() => handleSort('thc_mg_per_dollar')}>
                      THC mg/$
                      {getSortIcon('thc_mg_per_dollar')}
                    </div>
                  </th>
                  <th>
                    <div className="sort-header" onClick={() => handleSort('price_per_mg_thc')}>
                      $/mg THC
                      {getSortIcon('price_per_mg_thc')}
                    </div>
                  </th>
                  <th style={{ minWidth: '120px' }}>
                    <div className="sort-header" onClick={() => handleSort('avg_rating')}>
                      <Star size={14} style={{ color: '#f59e0b' }} />
                      Rating
                      {getSortIcon('avg_rating')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {leaderboardData.map((item, index) => {
                  const rank = (currentPage - 1) * 50 + index + 1;
                  const isExpanded = expandedRows[index];
                  const { line1, line2 } = formatProductName(item.product?.name);
                  return (
                    <>
                      <tr key={index} style={getRowStyle(rank)}>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>
                          {getRankIcon(rank)}
                        </td>
                        <td>
                          <div className="product-cell">
                            <img
                              src={item.product?.image || '/placeholder-product.jpg'}
                              alt={item.product?.name}
                              className="product-image"
                            />
                            <div className="product-info">
                              <h4>
                                {isExpanded ? item.product?.name : (
                                  <>
                                    {line1}
                                    {line2 && <div>{line2}</div>}
                                  </>
                                )}
                              </h4>
                              <p>{item.product?.brand}</p>
                              <button
                                className="expand-button"
                                onClick={() => toggleRow(index)}
                                aria-label={isExpanded ? 'Collapse product name' : 'Expand product name'}
                              >
                                {isExpanded ? 'Collapse' : 'Expand'}
                                {isExpanded ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          {item.review_count > 0 ? (
                            <SmokerLevelStats smokerReviews={item.smoker_reviews} />
                          ) : (
                            <div style={{
                              textAlign: 'center',
                              color: '#666',
                              fontSize: '12px',
                              padding: '8px'
                            }}>
                              No reviews
                            </div>
                          )}
                        </td>
                        <td>
                          <span style={{
                            background: 'rgba(0, 212, 170, 0.2)',
                            color: colors.textPrimary,
                            padding: '4px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            {item.product?.type || 'N/A'}
                          </span>
                        </td>
                        <td style={{ color: '#e5e5e5', fontWeight: '600' }}>
                          {item.thc_mg_total?.toFixed(1) || '0'}mg
                        </td>
                        <td style={{ color: colors.textPrimary, fontWeight: '600' }}>
                          ${item.price_dollars?.toFixed(2) || '0.00'}
                        </td>
                        <td style={{ 
                          color: rank <= 3 ? '#f59e0b' : '#e5e5e5', 
                          fontWeight: '700',
                          fontSize: rank <= 3 ? '16px' : '14px'
                        }}>
                          {item.thc_mg_per_dollar?.toFixed(1) || '0'}
                        </td>
                        <td style={{ color: colors.textSecondary }}>
                          ${item.price_per_mg_thc?.toFixed(3) || '0.000'}
                        </td>
                        <td>
                          {renderStarRating(item.avg_rating, item.review_count)}
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '32px',
            gap: '8px',
            flexWrap: 'wrap'
          }}>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 14px',
                    background: pageNum === currentPage 
                      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' 
                      : '#333',
                    color: pageNum === currentPage ? '#0a0a0a' : '#ccc',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: isMobile ? '12px' : '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    minWidth: isMobile ? '36px' : '40px'
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
        )}
        <THCCalculatorModal 
          isOpen={showCalculator}
          onClose={() => setShowCalculator(false)}
          leaderboardData={leaderboardData}
        />
      </div>
    </div>
  );
}